import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAPI } from '../lib/api';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  User,
  Building,
  Wallet,
  AlertTriangle,
  FileText,
  CreditCard,
  X,
  Loader2,
} from 'lucide-react';

const TYPE_ICONS = {
  entities: User,
  transactions: CreditCard,
  alerts: AlertTriangle,
  cases: FileText,
};

const TYPE_COLORS = {
  entities: 'bg-blue-500/10 text-blue-400 border-blue-400/30',
  transactions: 'bg-green-500/10 text-green-400 border-green-400/30',
  alerts: 'bg-red-500/10 text-red-400 border-red-400/30',
  cases: 'bg-purple-500/10 text-purple-400 border-purple-400/30',
};

export default function GlobalSearch({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      setResults(null);
      setSelectedIndex(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query.trim()) {
      setResults(null);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await searchAPI.global({ q: query, limit: 20 });
        setResults(response.data);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  // Flatten results for keyboard navigation
  const flatResults = results ? [
    ...results.entities.map(r => ({ ...r, _type: 'entities' })),
    ...results.transactions.map(r => ({ ...r, _type: 'transactions' })),
    ...results.alerts.map(r => ({ ...r, _type: 'alerts' })),
    ...results.cases.map(r => ({ ...r, _type: 'cases' })),
  ] : [];

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flatResults[selectedIndex]) {
      e.preventDefault();
      handleSelect(flatResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [flatResults, selectedIndex, onClose]);

  const handleSelect = (item) => {
    const routes = {
      entities: `/entities/${item.id}`,
      transactions: `/transactions/${item.id}`,
      alerts: `/alerts/${item.id}`,
      cases: `/cases/${item.id}`,
    };
    navigate(routes[item._type]);
    onClose();
  };

  const getItemLabel = (item) => {
    switch (item._type) {
      case 'entities': return item.name;
      case 'transactions': return item.transaction_ref;
      case 'alerts': return item.title;
      case 'cases': return item.title || item.case_number;
      default: return 'Unknown';
    }
  };

  const getItemSubLabel = (item) => {
    switch (item._type) {
      case 'entities': return `${item.entity_type} • ${item.country || 'N/A'}`;
      case 'transactions': return `${item.sender_name} → ${item.receiver_name}`;
      case 'alerts': return item.description?.slice(0, 50);
      case 'cases': return `${item.case_number} • ${item.status}`;
      default: return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass border-border max-w-2xl p-0 overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search entities, transactions, alerts, cases..."
              className="pl-10 pr-10 bg-black/20 border-white/10 text-lg h-12"
              data-testid="global-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {loading && (
            <div className="p-8 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
            </div>
          )}

          {!loading && results && results.total === 0 && (
            <div className="p-8 text-center">
              <Search className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">No results found for "{query}"</p>
            </div>
          )}

          {!loading && results && results.total > 0 && (
            <div className="divide-y divide-white/5">
              {['entities', 'transactions', 'alerts', 'cases'].map((type) => {
                const items = results[type];
                if (!items?.length) return null;

                const Icon = TYPE_ICONS[type];
                const globalIndex = flatResults.findIndex(r => r._type === type && r.id === items[0].id);

                return (
                  <div key={type}>
                    <div className="px-4 py-2 bg-white/[0.02]">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {type} ({items.length})
                      </p>
                    </div>
                    {items.slice(0, 5).map((item, i) => {
                      const itemIndex = globalIndex + i;
                      const isSelected = selectedIndex === itemIndex;
                      
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleSelect({ ...item, _type: type })}
                          className={`w-full p-3 flex items-center gap-3 hover:bg-white/5 transition-colors ${
                            isSelected ? 'bg-primary/10' : ''
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${TYPE_COLORS[type]}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <p className="text-white font-medium truncate">
                              {getItemLabel({ ...item, _type: type })}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {getItemSubLabel({ ...item, _type: type })}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-[9px] capitalize">
                            {type.slice(0, -1)}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !results && (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">Start typing to search...</p>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <Badge variant="outline" className="text-xs">⌘K to open</Badge>
                <Badge variant="outline" className="text-xs">↑↓ to navigate</Badge>
                <Badge variant="outline" className="text-xs">Enter to select</Badge>
                <Badge variant="outline" className="text-xs">Esc to close</Badge>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
